/**
 * The Restoration Project panel.
 *
 * Same shape and same ethics as the 1.02 patch panel: we link to where the
 * mod's author publishes it, the player downloads the archive themselves and
 * drops it back in, and nothing is hosted or proxied here. The differences are
 * scale — the RPU archive is ~830MB, so the drop gets a live progress line —
 * and consequence: installing removes the 1.02 patch (RPU carries those fixes)
 * and demands a new game, both of which are said before the drop, not after.
 */

import type { ImportProgress } from '../import';
import { RPU_RELEASES_URL, RpuError, getRpuMeta, installRpu, uninstallRpu } from '../import/rpu';
import { $, bytes, confirmDanger, note, toast } from './dom';

export interface RpuPanel {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

/**
 * @param onChanged fires after the vault changes shape in either direction —
 *   install or removal — so the caller can refresh the gate summary and badges.
 */
export function mountRpuPanel(onChanged: () => void): RpuPanel {
  const overlay = $('#overlay-rpu');
  const status = $('#rpu-status');
  const messages = $('#rpu-messages');
  const progress = $('#rpu-progress');
  const removeBtn = $<HTMLButtonElement>('#btn-rpu-remove');
  const input = $<HTMLInputElement>('#input-rpu');
  const dz = $('#rpu-dropzone');
  let busy = false;

  const mirrors = $('#rpu-mirrors');
  const row = document.createElement('div');
  row.className = 'mirror';
  const a = document.createElement('a');
  a.href = RPU_RELEASES_URL;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.textContent = 'RPU releases on GitHub';
  const span = document.createElement('span');
  span.textContent = 'BGforge’s official release page. Download the rpu_v*.zip under "Assets".';
  row.append(a, span);
  mirrors.replaceChildren(row);

  const refresh = async () => {
    const meta = await getRpuMeta();
    status.replaceChildren();
    removeBtn.hidden = !meta;
    dz.classList.toggle('done', !!meta);
    if (meta) {
      const version = meta.version === 'unknown' ? '' : ` v${meta.version}`;
      note(
        status,
        `Restoration Project${version} is installed — ${meta.mods.length} mod archive(s), ` +
          `${bytes(meta.totalBytes)}. Start a new game to play it.`,
        'ok'
      );
    }
  };

  const showProgress = (p: ImportProgress) => {
    progress.hidden = false;
    const file = p.file ? ` — ${p.file}` : '';
    const amount = p.bytesTotal ? ` (${bytes(p.bytesDone)} of ${bytes(p.bytesTotal)})` : '';
    progress.textContent = `${p.stage}${file}${amount}`;
  };

  const accept = async (file: File) => {
    if (busy) return;
    busy = true;
    messages.replaceChildren();
    try {
      const result = await installRpu(file, showProgress);
      progress.hidden = true;
      for (const w of result.warnings) note(messages, w, 'warn');
      const version = result.meta.version === 'unknown' ? '' : ` v${result.meta.version}`;
      note(
        messages,
        `Restoration Project${version} installed — ${result.meta.mods.length} mod archives, ${bytes(result.meta.totalBytes)}. ` +
          `Superseded files removed: ${result.removed.length ? result.removed.join(', ') : 'none'}. ` +
          'Start a new game to see it.',
        'ok'
      );
      toast('Restoration Project installed.', 'ok');
      await refresh();
      onChanged();
    } catch (e) {
      progress.hidden = true;
      const msg = e instanceof RpuError ? e.message : e instanceof Error ? e.message : String(e);
      note(messages, msg, 'bad');
      console.error('[rpu]', e);
    } finally {
      busy = false;
    }
  };

  $('#btn-rpu-pick').addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    if (f) void accept(f);
    input.value = '';
  });

  const hot = (on: boolean) => dz.classList.toggle('hot', on);
  for (const type of ['dragenter', 'dragover'] as const) {
    dz.addEventListener(type, (e) => {
      e.preventDefault();
      hot(true);
    });
  }
  for (const type of ['dragleave', 'dragend'] as const) dz.addEventListener(type, () => hot(false));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    hot(false);
    const f = (e as DragEvent).dataTransfer?.files?.[0];
    if (f) void accept(f);
  });

  removeBtn.addEventListener('click', () => {
    if (busy) return;
    void (async () => {
      if (
        !(await confirmDanger(
          'Remove the Restoration Project? Its mod archives leave the vault, and because installing it ' +
            'replaced the 1.02 patch, you will be back on an unpatched game until you re-add patch000.dat ' +
            'or re-import. Saves made with RPU will not work without it.'
        ))
      )
        return;
      busy = true;
      messages.replaceChildren();
      try {
        const removed = await uninstallRpu();
        note(messages, `Removed ${removed.length} file(s). Re-add the 1.02 patch from the gate badge.`, 'ok');
        toast('Restoration Project removed.', 'warn');
        await refresh();
        onChanged();
      } catch (e) {
        note(messages, e instanceof Error ? e.message : String(e), 'bad');
      } finally {
        busy = false;
      }
    })();
  });

  const panel: RpuPanel = {
    open() {
      messages.replaceChildren();
      progress.hidden = true;
      void refresh();
      overlay.classList.add('open');
    },
    close() {
      overlay.classList.remove('open');
    },
    get isOpen() {
      return overlay.classList.contains('open');
    },
  };

  $('#btn-rpu-close').addEventListener('click', () => panel.close());

  return panel;
}
